import { FunctionComponent } from 'react';
import { Box, Button, ButtonBase, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import { PIMCORE_CONTENT_BASE_URL } from '../../../utils/Pimcore';
import { useResourceTypes } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceTypesProvider';
import { useBrands } from '../../../graphql/pimcore/modules/resourceLibrary/contexts/BrandsProvider';
import type { ResourceLibraryItem } from '../../../graphql/pimcore/modules/resourceLibrary/types/resourceLibraryItems';
import {
    isArticleItem,
    isAssetItem,
    isInternalLinkItem,
    isNewsItem,
} from '../../../graphql/pimcore/modules/resourceLibrary/types/resourceLibraryItems';
import { useLocale } from '../../../contexts/LocaleProvider';
import { ENABLED_LOCALES, Locale, LocaleCode } from '../../../utils/Locale';
import type { OpenResourceLibraryItemProps } from '../../../utils/openResourceLibraryItem';
import { openResourceLibraryItem } from '../../../utils/openResourceLibraryItem';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import { Tooltip } from 'antd';
import type {
    CommonResourceLibraryContent,
    CommonResourceLibraryDateLabelsContent,
} from '../../../graphql/pimcore/modules/siteContent/types/CommonContent';
import { useCommonContent } from '../../../graphql/pimcore/modules/siteContent/hooks/useCommonContent';

// When to hide language variants buttons
export type HideLanguageVariantsCondition =
    | 'always'
    | 'never'
    | 'if-only-one'
    | 'if-only-one-in-current-language'
    | 'if-exists-for-current-language';

// When to hide the "View Document" (assets) or "Navigate" (links) clickable label
export type HideViewAssetLabelCondition = 'always' | 'never' | 'if-hide-language-variants';

interface ResourcesCardProps {
    item: ResourceLibraryItem & { missingSearchTerms?: string[] };
    view?: string;
    backgroundColor?: string;
    showThumbnail?: boolean;
    clickableHeader?: boolean; // clicking on header opens the asset in primary language
    displayAssetLinks?: boolean;
    hideLanguageVariantsCondition?: HideLanguageVariantsCondition;
    hideViewAssetLabelCondition?: HideViewAssetLabelCondition;
}

// HeaderRow Component
interface HeaderRowProps {
    name: string;
    typeName: string;
    clickable: boolean;
    onOpen: () => void;
    showTypeName?: boolean;
}
const HeaderRow: FunctionComponent<HeaderRowProps> = ({
    name,
    typeName,
    clickable,
    onOpen,
    showTypeName = true,
}) => {
    function truncateText(text: string, maxLength: number): string {
        if (!text) return '';
        return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
    }
    const { isScreenSm, isScreenXs } = useResponsive();
    const isSmallScreen = isScreenSm || isScreenXs;
    const headerContent = (
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" width="100%">
            <Tooltip title={name}>
                <Typography variant="h6" color="#B75332" sx={{ lineHeight: 1.2 }}>
                    {isSmallScreen ? truncateText(name, 48) : name}
                </Typography>
            </Tooltip>
            {showTypeName && (
                <Typography
                    sx={{
                        color: '#555',
                        bgcolor: 'white',
                        padding: '2px 8px',
                        border: '1px solid #7A7C81',
                        borderRadius: '16px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        marginLeft: 'auto',
                        whiteSpace: 'nowrap',
                    }}
                    component="span"
                >
                    {typeName}
                </Typography>
            )}
        </Box>
    );
    return clickable ? (
        <ButtonBase onClick={onOpen} sx={{ width: '100%', textAlign: 'left' }}>
            {headerContent}
        </ButtonBase>
    ) : (
        headerContent
    );
};

// Thumbnail Component
interface ThumbnailProps {
    item: ResourceLibraryItem;
    onOpen: ({ item, lang }: OpenResourceLibraryItemProps) => void;
    size?: number | string;
}
const Thumbnail: FunctionComponent<ThumbnailProps> = ({ item, onOpen, size }) => {
    return (
        <ButtonBase onClick={() => onOpen({ item })}>
            <Box
                component="img"
                src={item.thumbnailUrl ? PIMCORE_CONTENT_BASE_URL + item.thumbnailUrl : ''}
                alt={item.name}
                sx={{
                    width: size || 120,
                    height: 'auto',
                    maxWidth: '100%',
                    objectFit: 'contain',
                }}
            />
        </ButtonBase>
    );
};

// DescriptionRow Component
interface DescriptionRowProps {
    description: string;
}
const DescriptionRow: FunctionComponent<DescriptionRowProps> = ({ description }) => (
    <Typography
        variant="body2"
        sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
        }}
    >
        {description}
    </Typography>
);

interface AssetLinksProps {
    item: ResourceLibraryItem;
    hideLanguageVariantsCondition: HideLanguageVariantsCondition;
    hideViewAssetLabelCondition: HideViewAssetLabelCondition;
    localeCode: LocaleCode;
    onOpen: ({ item, lang }: OpenResourceLibraryItemProps) => void;
    cmsContent: CommonResourceLibraryContent;
}

const AssetLinks: FunctionComponent<AssetLinksProps> = ({
    item,
    hideLanguageVariantsCondition,
    hideViewAssetLabelCondition,
    localeCode,
    onOpen,
    cmsContent,
}) => {
    if (!isAssetItem(item)) {
        return null;
    }

    const asset = item.asset;
    if (!asset) {
        return null;
    }

    const assetByLanguage = item.assetByLanguage;

    // determine what to show
    const assetCount = ENABLED_LOCALES.filter(
        (locale: Locale) => !!assetByLanguage[locale.locale]
    ).filter(Boolean).length;
    const currentLanguageAsset = assetByLanguage[localeCode];
    const isOnlyOneInCurrentLanguage = assetCount === 1 && currentLanguageAsset;

    const hideLanguageVariants =
        hideLanguageVariantsCondition === 'always' ||
        (hideLanguageVariantsCondition === 'if-only-one' && assetCount === 1) ||
        (hideLanguageVariantsCondition === 'if-only-one-in-current-language' &&
            isOnlyOneInCurrentLanguage) ||
        (hideLanguageVariantsCondition === 'if-exists-for-current-language' &&
            !currentLanguageAsset);
    const hideViewAssetLabel =
        hideViewAssetLabelCondition === 'always' ||
        (hideViewAssetLabelCondition === 'if-hide-language-variants' && hideLanguageVariants);

    return (
        <Box>
            {!hideViewAssetLabel && (
                <OpenResourceLibraryItemButton
                    item={item}
                    label={
                        cmsContent.buttonLabels.openItem.asset.content || 'View Document' // Fallback label
                    }
                />
            )}
            {!hideLanguageVariants &&
                ENABLED_LOCALES.map((locale: Locale) => {
                    const localeCode = locale.locale;
                    const assetLang = assetByLanguage[localeCode];
                    if (!assetLang) {
                        return null;
                    }

                    return (
                        <Button
                            key={localeCode}
                            sx={{
                                marginRight: 2,
                                padding: '4px 6px',
                                color: '#555',
                                border: '0.5px solid #7A7C81',
                                borderRadius: '10px',
                                fontSize: '0.66rem',
                                fontWeight: 400,
                                textTransform: 'uppercase',
                                minWidth: 'auto',
                                whiteSpace: 'nowrap',
                            }}
                            size="small"
                            onClick={() => onOpen({ item, lang: localeCode })}
                        >
                            {locale.name}
                        </Button>
                    );
                }).filter(Boolean)}
        </Box>
    );
};
function getFormattedDate(
    item: ResourceLibraryItem,
    locale: LocaleCode,
    cmsContent: CommonResourceLibraryDateLabelsContent
): string {
    let dateLabel = '';
    let formattedDate = '';

    if (item.expirationDate) {
        dateLabel = cmsContent.expiration.content;
        formattedDate = new Date(item.expirationDate).toLocaleDateString(locale);
    } else if (item.validFromDate) {
        dateLabel = cmsContent.validFrom.content;
        formattedDate = new Date(item.validFromDate).toLocaleDateString(locale);
    } else if (item.lastModificationDate) {
        dateLabel = cmsContent.lastModification.content;
        formattedDate = new Date(item.lastModificationDate).toLocaleDateString(locale);
    } else if (item.publicationDate) {
        dateLabel = cmsContent.publication.content;
        formattedDate = new Date(item.publicationDate).toLocaleDateString(locale);
    }

    return dateLabel && formattedDate ? `${dateLabel}: ${formattedDate}` : '';
}
// ActionRow Component
interface ActionRowProps {
    item: ResourceLibraryItem;
    onOpen: ({ item, lang }: OpenResourceLibraryItemProps) => void;
    displayAssetLinks: boolean;
    hideLanguageVariantsCondition: HideLanguageVariantsCondition;
    hideViewAssetLabelCondition: HideViewAssetLabelCondition;
    cmsContent: CommonResourceLibraryContent;
}
const ActionRow: FunctionComponent<ActionRowProps> = ({
    item,
    onOpen,
    displayAssetLinks,
    hideLanguageVariantsCondition,
    hideViewAssetLabelCondition,
    cmsContent,
}) => {
    const { locale } = useLocale();
    const { brandById } = useBrands();
    // date
    const formattedDate = getFormattedDate(item, locale, cmsContent.dateLabels);
    // brand
    const brand = item.brandId ? brandById[item.brandId] : null;
    const brandLogoUrl =
        brand && brand.logo && brand.logo.fullpath
            ? PIMCORE_CONTENT_BASE_URL + brand.logo.fullpath
            : '';

    return (
        <Grid
            container
            spacing={1}
            alignItems="flex-start"
            justifyContent="space-between"
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ paddingTop: 0 }}
        >
            <Grid item xs>
                <Grid container direction="column" spacing={1}>
                    {isAssetItem(item) && item.asset && displayAssetLinks && (
                        <Grid item>
                            <AssetLinks
                                item={item}
                                hideLanguageVariantsCondition={hideLanguageVariantsCondition}
                                hideViewAssetLabelCondition={hideViewAssetLabelCondition}
                                localeCode={locale}
                                onOpen={onOpen}
                                cmsContent={cmsContent}
                            />
                        </Grid>
                    )}
                    {isInternalLinkItem(item) && hideViewAssetLabelCondition === 'never' && (
                        <Grid item>
                            <OpenResourceLibraryItemButton
                                item={item}
                                label={cmsContent.buttonLabels.openItem.internalLink.content}
                            />
                        </Grid>
                    )}
                    {isArticleItem(item) && hideViewAssetLabelCondition === 'never' && (
                        <Grid item>
                            <OpenResourceLibraryItemButton
                                item={item}
                                label={cmsContent.buttonLabels.openItem.article.content}
                            />
                        </Grid>
                    )}
                    {isNewsItem(item) && hideViewAssetLabelCondition === 'never' && (
                        <Grid item>
                            <OpenResourceLibraryItemButton
                                item={item}
                                label={cmsContent.buttonLabels.openItem.news.content}
                            />
                        </Grid>
                    )}
                    {formattedDate && (
                        <Grid item>
                            <Typography variant="caption">{formattedDate}</Typography>
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Grid item>
                {brandLogoUrl && (
                    <Box
                        component="img"
                        src={brandLogoUrl}
                        alt={brand?.name || ''}
                        sx={{ maxWidth: 100, width: '100%' }}
                    />
                )}
            </Grid>
        </Grid>
    );
};

// MissingTermsRow Component
interface MissingTermsRowProps {
    missingTerms: string[];
    cmsContent: CommonResourceLibraryContent;
}
const MissingTermsRow: FunctionComponent<MissingTermsRowProps> = ({ missingTerms, cmsContent }) => (
    <Typography variant="caption" display="block">
        {cmsContent.missingSearchTermsLabel.content}:{' '}
        <span style={{ fontWeight: 'bold', textDecoration: 'line-through' }}>
            {missingTerms.join(', ')}
        </span>
    </Typography>
);

const ResourcesCard: FunctionComponent<ResourcesCardProps> = ({
    item,
    backgroundColor,
    showThumbnail = true,
    clickableHeader = true,
    displayAssetLinks = true,
    hideLanguageVariantsCondition = 'never',
    hideViewAssetLabelCondition = 'never',
}) => {
    const { resourceTypeById } = useResourceTypes();
    const { brandById } = useBrands();
    const { locale } = useLocale();
    const { isScreenMd, isScreenSm, isScreenXs } = useResponsive();
    const { contentTree: cmsCommonContent } = useCommonContent();
    const cmsContent = cmsCommonContent.ResourceLibrary;

    const typeName = resourceTypeById[item.resourceTypeId]?.name ?? 'N/A';
    const brand = item.brandId ? brandById[item.brandId] : null;
    const brandLogoUrl =
        brand && brand.logo && brand.logo.fullpath
            ? PIMCORE_CONTENT_BASE_URL + brand.logo.fullpath
            : '';
    const formattedDate = getFormattedDate(item, locale, cmsContent.dateLabels);
    const isSmallScreen = isScreenMd || isScreenSm || isScreenXs;

    if (isSmallScreen) {
        return (
            <Card variant="outlined" sx={{ mt: 1, border: 'none', width: '100%' }}>
                <CardContent sx={{ padding: '5px', width: '100%', backgroundColor }}>
                    <Stack spacing={1} direction="row">
                        {showThumbnail && item.thumbnailUrl && (
                            <Box sx={{ minWidth: 120, maxWidth: 120 }}>
                                <Stack spacing={2} alignItems="center">
                                    <Thumbnail
                                        item={item}
                                        onOpen={openResourceLibraryItem}
                                        size={120}
                                    />
                                    <Tooltip title={typeName}>
                                        <Box
                                            sx={{
                                                color: '#555',
                                                bgcolor: 'white',
                                                padding: '2px 8px',
                                                border: '1px solid #7A7C81',
                                                borderRadius: '16px',
                                                fontSize: '12px',
                                                fontWeight: 400,
                                                textTransform: 'uppercase',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: 120,
                                                cursor: 'default',
                                            }}
                                        >
                                            {typeName}
                                        </Box>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        )}

                        <Stack spacing={1}>
                            <HeaderRow
                                name={item.name}
                                typeName={typeName}
                                clickable={clickableHeader}
                                onOpen={() => openResourceLibraryItem({ item })}
                                showTypeName={false}
                            />

                            <DescriptionRow description={item.description} />

                            {/* Action buttons (asset link, navigate, read more) */}
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <Stack spacing={1} sx={{ flex: 1 }}>
                                    {isAssetItem(item) && displayAssetLinks && (
                                        <AssetLinks
                                            item={item}
                                            hideLanguageVariantsCondition={
                                                hideLanguageVariantsCondition
                                            }
                                            hideViewAssetLabelCondition={
                                                hideViewAssetLabelCondition
                                            }
                                            localeCode={locale}
                                            onOpen={openResourceLibraryItem}
                                            cmsContent={cmsContent}
                                        />
                                    )}
                                    {isInternalLinkItem(item) &&
                                        hideViewAssetLabelCondition === 'never' && (
                                            <OpenResourceLibraryItemButton
                                                item={item}
                                                label={
                                                    cmsContent.buttonLabels.openItem.internalLink
                                                        .content
                                                }
                                            />
                                        )}
                                    {isArticleItem(item) &&
                                        hideViewAssetLabelCondition === 'never' && (
                                            <OpenResourceLibraryItemButton
                                                item={item}
                                                label={
                                                    cmsContent.buttonLabels.openItem.article.content
                                                }
                                            />
                                        )}
                                    {isNewsItem(item) &&
                                        hideViewAssetLabelCondition === 'never' && (
                                            <OpenResourceLibraryItemButton
                                                item={item}
                                                label={
                                                    cmsContent.buttonLabels.openItem.news.content
                                                }
                                            />
                                        )}
                                </Stack>
                            </Stack>

                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="wrap"
                                spacing={1}
                            >
                                {formattedDate && (
                                    <Typography variant="caption" sx={{ color: '#555' }}>
                                        {formattedDate}
                                    </Typography>
                                )}

                                {brandLogoUrl && (
                                    <Box
                                        component="img"
                                        src={brandLogoUrl}
                                        alt={brand?.name || ''}
                                        sx={{ maxWidth: 80, width: '100%' }}
                                    />
                                )}
                            </Stack>

                            {item.missingSearchTerms && item.missingSearchTerms.length > 0 && (
                                <MissingTermsRow
                                    missingTerms={item.missingSearchTerms}
                                    cmsContent={cmsContent}
                                />
                            )}
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card
            variant="outlined"
            sx={{
                marginTop: 1,
                border: 'none',
                width: '100%',
            }}
        >
            <CardContent sx={{ backgroundColor: backgroundColor }}>
                <Grid
                    container
                    spacing={2}
                    alignItems="flex-start"
                    direction={{ xs: 'column', sm: 'row' }}
                    width={'100%'}
                >
                    {showThumbnail && item.thumbnailUrl ? (
                        <Grid item xs="auto">
                            <Thumbnail item={item} onOpen={openResourceLibraryItem} />
                        </Grid>
                    ) : null}
                    <Grid item xs>
                        <Grid container direction="column" spacing={1}>
                            <Grid item>
                                <HeaderRow
                                    name={item.name}
                                    typeName={typeName}
                                    clickable={clickableHeader}
                                    onOpen={() => openResourceLibraryItem({ item })}
                                />
                            </Grid>
                            <Grid item>
                                <DescriptionRow description={item.description} />
                            </Grid>
                            <Grid item>
                                <ActionRow
                                    item={item}
                                    onOpen={openResourceLibraryItem}
                                    displayAssetLinks={displayAssetLinks}
                                    hideLanguageVariantsCondition={hideLanguageVariantsCondition}
                                    hideViewAssetLabelCondition={hideViewAssetLabelCondition}
                                    cmsContent={cmsContent}
                                />
                            </Grid>
                            {item.missingSearchTerms && item.missingSearchTerms.length > 0 && (
                                <Grid item>
                                    <MissingTermsRow
                                        missingTerms={item.missingSearchTerms}
                                        cmsContent={cmsContent}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const OpenResourceLibraryItemButton = ({
    item,
    label,
}: {
    item: ResourceLibraryItem;
    label: string;
}) => {
    return (
        <Button
            sx={{ p: 0, color: '#B75332', marginRight: 2 }}
            size="small"
            onClick={() => openResourceLibraryItem({ item })}
        >
            {label}
        </Button>
    );
};

export default ResourcesCard;
